/**
 * Households domain API (Spec 059, E2). Moved bitgleich from the former monolithic `api.ts` — no
 * behavior change (E5).
 */
import type { ApiCore } from './core';

export type HouseholdType = 'Privat' | 'Gewerbe' | 'VuV';

export interface Household {
  id: string;
  name?: string;
  type: HouseholdType;
  members: HouseholdMember[];
  createdAt: string;
  updatedAt?: string;
}

export interface HouseholdMember {
  id: string;
  name: string;
  role?: string;
  createdAt: string;
}

export class HouseholdsApi {
  constructor(private readonly core: ApiCore) {}

  /**
   * Get household with all members
   */
  async getHousehold(): Promise<Household> {
    return this.core.fetchJson<Household>(`${this.core.baseUrl}/api/v1/household`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Get all household members
   */
  async getHouseholdMembers(): Promise<HouseholdMember[]> {
    return this.core.fetchJson<HouseholdMember[]>(`${this.core.baseUrl}/api/v1/household/members`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Add a new household member
   */
  async addHouseholdMember(name: string, role?: string): Promise<HouseholdMember> {
    return this.core.fetchJson<HouseholdMember>(`${this.core.baseUrl}/api/v1/household/members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify({ name, role }),
    });
  }

  /**
   * Update a household member
   */
  async updateHouseholdMember(id: string, name: string, role?: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/household/members/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify({ name, role }),
    });
  }

  /**
   * Remove a household member
   */
  async removeHouseholdMember(id: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/household/members/${id}`, {
      method: 'DELETE',
      headers: this.core.getAuthHeaders(),
    });
  }

  // === Multi-Household API (plural /households) ===

  /**
   * Get all households
   */
  async getHouseholds(): Promise<Household[]> {
    return this.core.fetchJson<Household[]>(`${this.core.baseUrl}/api/v1/households`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Get household by ID
   */
  async getHouseholdById(id: string): Promise<Household> {
    return this.core.fetchJson<Household>(`${this.core.baseUrl}/api/v1/households/${id}`, {
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Create a new household
   */
  async createHousehold(name: string, type: HouseholdType): Promise<Household> {
    return this.core.fetchJson<Household>(`${this.core.baseUrl}/api/v1/households`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify({ name, type }),
    });
  }

  /**
   * Update a household
   */
  async updateHousehold(id: string, data: { name?: string; type?: HouseholdType }): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/households/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a household (must have no documents)
   */
  async deleteHousehold(id: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/households/${id}`, {
      method: 'DELETE',
      headers: this.core.getAuthHeaders(),
    });
  }

  /**
   * Move a document to a different household
   */
  async moveDocumentToHousehold(documentId: string, householdId: string): Promise<void> {
    return this.core.fetchVoid(`${this.core.baseUrl}/api/v1/documents/${documentId}/household`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...this.core.getAuthHeaders(),
      },
      body: JSON.stringify({ householdId }),
    });
  }
}
